import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Pill, Printer, AlertTriangle, Package, Filter, CheckCircle, Send, PackagePlus, ScanLine, Plus, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { openPrintWindow } from '@/lib/letterhead';
import QRScanner from '@/components/QRScanner';
import RicevimentoFarmaciDialog from '@/components/RicevimentoFarmaciDialog';
import FarmacoAutocomplete from '@/components/FarmacoAutocomplete';
import DatePickerField from '@/components/DatePickerField';

export default function OrdiniFarmaci() {
  const { hasRole } = useAuth();
  const [items, setItems] = useState([]);
  const [mmgList, setMmgList] = useState([]);
  const [selectedMmg, setSelectedMmg] = useState('tutti');
  const [selectedArea, setSelectedArea] = useState('tutte');
  const [groupBy, setGroupBy] = useState('mmg'); // mmg | area
  const [loading, setLoading] = useState(true);
  const [showRifornimento, setShowRifornimento] = useState(false);
  const [rifItem, setRifItem] = useState(null);
  const [rifScatole, setRifScatole] = useState('');
  const [rifUnitaConf, setRifUnitaConf] = useState('');
  const [rifManuale, setRifManuale] = useState(false);
  const [ricevimentoOpen, setRicevimentoOpen] = useState(false);
  const [ricevimentoOspiteId, setRicevimentoOspiteId] = useState(null);
  // New therapy dialog
  const [showNewTerapia, setShowNewTerapia] = useState(false);
  const [ospiti, setOspiti] = useState([]);
  const [newForm, setNewForm] = useState({ ospite_id: '', farmaco: '', dosaggio: '', via: 'orale', al_bisogno: false, orari: [], durata_inizio: new Date().toISOString().slice(0, 10), durata_fine: 'mai', principio_attivo: '', confezione_unita: '', scorta_attuale: '', soglia_riordino: '7', note: '' });
  const ORARI_PRESET = ['06:00','07:00','08:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00','18:00','19:00','20:00','21:00','22:00'];

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedMmg && selectedMmg !== 'tutti') params.mmg_id = selectedMmg;
      const res = await api.get('/scorte/in-esaurimento', { params });
      setItems(res.data);
    } catch { setItems([]); }
    setLoading(false);
  }, [selectedMmg]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    api.get('/utility/mmg').then(r => setMmgList(r.data)).catch(() => {});
    api.get('/ospiti', { params: { stato: 'presente' } }).then(r => setOspiti(r.data)).catch(() => {});
  }, []);

  const handleSegnaRichiesta = async (terapiaId) => {
    try {
      await api.put(`/scorte/${terapiaId}/richiesta`);
      toast.success('Richiesta segnata');
      fetchData();
    } catch { toast.error('Errore'); }
  };

  const openRifornimento = (item) => {
    setRifItem(item);
    const confUnita = item.confezione_unita || 0;
    setRifUnitaConf(confUnita > 0 ? String(confUnita) : '');
    setRifScatole('1');
    setRifManuale(confUnita === 0);
    setShowRifornimento(true);
  };

  const rifTotaleUnita = () => {
    const scatole = parseInt(rifScatole) || 0;
    const unita = parseInt(rifUnitaConf) || 0;
    return scatole * unita;
  };

  const handleRifornimento = async () => {
    const totale = rifTotaleUnita();
    if (totale <= 0) { toast.error('Inserisci quantita valida (scatole x unita)'); return; }
    try {
      const payload = { quantita: totale };
      const unitaConf = parseInt(rifUnitaConf) || 0;
      if (unitaConf > 0) payload.confezione_unita = unitaConf;
      await api.put(`/scorte/${rifItem.terapia_id}/rifornimento`, payload);
      toast.success(`Scorta aggiornata: +${totale} unita (${rifScatole} conf.)`);
      setShowRifornimento(false);
      fetchData();
    } catch { toast.error('Errore aggiornamento scorta'); }
  };

  // Filter by area
  const filteredItems = selectedArea === 'tutte' ? items : items.filter(i => (i.area || '') === selectedArea);

  // Group by MMG
  const groupedByMmg = {};
  filteredItems.forEach(item => {
    const key = item.mmg_id || '_nommg';
    const label = item.mmg_nome || 'MMG non assegnato';
    if (!groupedByMmg[key]) groupedByMmg[key] = { label, items: [] };
    groupedByMmg[key].items.push(item);
  });

  // Group by Area
  const groupedByArea = {};
  filteredItems.forEach(item => {
    const key = item.area || '_noarea';
    const label = key === 'verde' ? 'Area Verde' : key === 'gialla' ? 'Area Gialla' : 'Area Non Assegnata';
    if (!groupedByArea[key]) groupedByArea[key] = { label, items: [] };
    groupedByArea[key].items.push(item);
  });

  const grouped = groupBy === 'mmg' ? groupedByMmg : groupedByArea;

  const richiesti = filteredItems.filter(i => i.richiesta_inviata).length;
  const daRichiedere = filteredItems.filter(i => !i.richiesta_inviata).length;

  const handleQRScan = (ospiteId) => {
    setRicevimentoOspiteId(ospiteId);
    setRicevimentoOpen(true);
  };

  // New therapy save
  const handleSaveNewTerapia = async () => {
    if (!newForm.ospite_id || !newForm.farmaco || !newForm.dosaggio) {
      toast.error('Seleziona ospite, farmaco e dosaggio'); return;
    }
    if (!newForm.al_bisogno && newForm.orari.length === 0) {
      toast.error('Seleziona almeno un orario oppure imposta come al bisogno'); return;
    }
    try {
      await api.post('/terapie', {
        ...newForm,
        orari: newForm.al_bisogno ? [] : newForm.orari,
        durata_fine: newForm.durata_fine === 'mai' ? '' : newForm.durata_fine,
        confezione_unita: parseInt(newForm.confezione_unita) || 0,
        scorta_attuale: parseInt(newForm.scorta_attuale) || 0,
        soglia_riordino: parseInt(newForm.soglia_riordino) || 7,
      });
      toast.success('Terapia aggiunta');
      setShowNewTerapia(false);
      setNewForm({ ospite_id: '', farmaco: '', dosaggio: '', via: 'orale', al_bisogno: false, orari: [], durata_inizio: new Date().toISOString().slice(0, 10), durata_fine: 'mai', principio_attivo: '', confezione_unita: '', scorta_attuale: '', soglia_riordino: '7', note: '' });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore creazione terapia'); }
  };

  const toggleOrario = (o) => setNewForm(f => ({ ...f, orari: f.orari.includes(o) ? f.orari.filter(x => x !== o) : [...f.orari, o] }));

  const detectViaAndUnit = (forme) => {
    if (!forme || forme.length === 0) return { via: 'orale', unitLabel: 'compressa', placeholder: 'es. 1 compressa' };
    const f = forme[0].toLowerCase();
    if (f.includes('iniettabile') || f.includes('infusione') || f.includes('siringa') || f.includes('penna pre-riempita')) {
      if (f.includes('sottocutan') || f.includes('penna')) return { via: 'sottocutanea', unitLabel: f.includes('penna') ? 'dose penna' : 'fiala', placeholder: 'es. 1 fiala' };
      if (f.includes('endoven') || f.includes('infusione')) return { via: 'endovenosa', unitLabel: 'fiala', placeholder: 'es. 1 fiala in 100ml SF' };
      return { via: 'intramuscolare', unitLabel: 'fiala', placeholder: 'es. 1 fiala' };
    }
    if (f.includes('compressa') || f.includes('confetto')) {
      let unit = 'compressa';
      if (f.includes('effervescen')) unit = 'compressa effervescente';
      else if (f.includes('orodispersi') || f.includes('orosolubil') || f.includes('dispersibil')) unit = 'compressa dispersibile';
      else if (f.includes('masticabil')) unit = 'compressa masticabile';
      else if (f.includes('rilascio modificato')) unit = 'compressa a rilascio modificato';
      else if (f.includes('rilascio prolungato')) unit = 'compressa a rilascio prolungato';
      else if (f.includes('gastroresisten')) unit = 'compressa gastroresistente';
      else if (f.includes('rivestit')) unit = 'compressa rivestita';
      else if (f.includes('sublinguale')) return { via: 'sublinguale', unitLabel: 'compressa sublinguale', placeholder: 'es. 1 compressa sublinguale' };
      else if (f.includes('confetto')) unit = 'confetto';
      return { via: 'orale', unitLabel: unit, placeholder: `es. 1 ${unit}` };
    }
    if (f.includes('capsula')) {
      let unit = 'capsula';
      if (f.includes('molle') || f.includes('molli')) unit = 'capsula molle';
      else if (f.includes('rigida')) unit = 'capsula rigida';
      else if (f.includes('gastroresisten')) unit = 'capsula gastroresistente';
      else if (f.includes('rilascio')) unit = 'capsula a rilascio modificato';
      return { via: 'orale', unitLabel: unit, placeholder: `es. 1 ${unit}` };
    }
    if (f.includes('sciroppo')) return { via: 'orale', unitLabel: 'ml di sciroppo', placeholder: 'es. 5 ml' };
    if (f.includes('gocce orali') || f.includes('gocce per')) return { via: 'orale', unitLabel: 'gocce', placeholder: 'es. 10 gocce' };
    if (f.includes('sospensione orale') || f.includes('soluzione orale')) return { via: 'orale', unitLabel: 'ml', placeholder: 'es. 5 ml' };
    if (f.includes('granulato effervescen')) return { via: 'orale', unitLabel: 'bustina effervescente', placeholder: 'es. 1 bustina' };
    if (f.includes('granulato') || f.includes('bustina')) return { via: 'orale', unitLabel: 'bustina', placeholder: 'es. 1 bustina' };
    if (f.includes('supposta')) return { via: 'rettale', unitLabel: 'supposta', placeholder: 'es. 1 supposta' };
    if (f.includes('rettale')) return { via: 'rettale', unitLabel: 'dose rettale', placeholder: 'es. 1 microclisma' };
    if (f.includes('inalazi') || f.includes('nebulizz') || f.includes('aerosol')) return { via: 'inalatoria', unitLabel: 'puff', placeholder: 'es. 2 puff' };
    if (f.includes('crema')) return { via: 'topica', unitLabel: 'applicazione di crema', placeholder: 'es. 1 applicazione' };
    if (f.includes('gel')) return { via: 'topica', unitLabel: 'applicazione di gel', placeholder: 'es. 1 applicazione' };
    if (f.includes('pomata') || f.includes('unguento')) return { via: 'topica', unitLabel: 'applicazione di pomata', placeholder: 'es. 1 applicazione' };
    if (f.includes('cerotto') || f.includes('patch')) return { via: 'transdermica', unitLabel: 'cerotto', placeholder: 'es. 1 cerotto' };
    if (f.includes('collirio') || f.includes('oftalmico')) return { via: 'oftalmica', unitLabel: 'goccia oculare', placeholder: 'es. 1 goccia per occhio' };
    return { via: 'orale', unitLabel: 'unita', placeholder: 'es. 1 compressa / 1 fiala / 5 ml' };
  };

  const handlePrint = (key) => {
    const group = grouped[key];
    if (!group) return;
    const rows = group.items.map(i =>
      `<tr style="${i.richiesta_inviata ? 'background:#f0fdf4' : ''}"><td>${i.ospite_nome}</td><td>${i.stanza || '-'}/${i.letto || '-'} ${i.area ? '(' + i.area + ')' : ''}</td><td><strong>${i.farmaco}</strong></td><td>${i.dosaggio}</td><td>${i.principio_attivo || '-'}</td><td>${i.scorta_attuale}</td><td>${i.giorni_rimasti} gg</td><td>${i.richiesta_inviata ? '<span style="color:#16a34a;font-weight:bold">Richiesto</span>' : 'Da richiedere'}</td></tr>`
    ).join('');
    const subtitle = groupBy === 'mmg' ? `Dott. ${group.label}` : group.label;
    const body = `
      <div class="info-grid"><div class="info-item"><span class="info-label">${groupBy === 'mmg' ? 'Medico' : 'Area'}</span><span class="info-value">${subtitle}</span></div><div class="info-item"><span class="info-label">Farmaci da richiedere</span><span class="info-value">${group.items.length}</span></div></div>
      <table><thead><tr><th>Ospite</th><th>Camera/Area</th><th>Farmaco</th><th>Dosaggio</th><th>Principio Attivo</th><th>Scorta</th><th>Giorni Rim.</th><th>Stato</th></tr></thead><tbody>${rows}</tbody></table>
      <div class="signature-row"><div class="signature-block">Firma Coordinatore</div><div class="signature-block">Firma Medico</div></div>`;
    openPrintWindow('Richiesta Prescrizione Farmaci', body, { subtitle });
  };

  const areaColors = { verde: 'bg-emerald-100 text-emerald-700 border-emerald-300', gialla: 'bg-amber-100 text-amber-700 border-amber-300', _noarea: 'bg-slate-100 text-slate-500 border-slate-300' };

  return (
    <div className="space-y-6" data-testid="ordini-farmaci-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900 font-[Cinzel]">Ordini Farmaci</h1>
          <p className="text-sm text-slate-500">Farmaci in esaurimento da richiedere al medico</p>
          {filteredItems.length > 0 && (
            <div className="flex gap-2 mt-1">
              <Badge className="bg-amber-100 text-amber-700 text-[10px]">{daRichiedere} da richiedere</Badge>
              {richiesti > 0 && <Badge className="bg-emerald-100 text-emerald-700 text-[10px]"><CheckCircle className="w-3 h-3 mr-0.5" />{richiesti} richiesti</Badge>}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <QRScanner onScanResult={handleQRScan} buttonLabel="Scansiona Ospite" className="text-brand border-blue-200 hover:bg-blue-50" testId="qr-ricevimento-btn" />
          {hasRole('medico', 'coordinatore') && (
            <Button size="sm" onClick={() => setShowNewTerapia(true)} className="bg-brand hover:bg-brand-dark text-white" data-testid="nuova-terapia-ordini-btn">
              <Plus className="w-4 h-4 mr-1" />Nuova Terapia
            </Button>
          )}
        </div>
      </div>

      {/* Filters row */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-slate-500 font-medium">Raggruppa:</span>
          <button onClick={() => setGroupBy('mmg')} className={`px-2.5 py-1 text-[10px] rounded border transition-all ${groupBy === 'mmg' ? 'bg-brand text-white border-brand' : 'bg-white text-slate-500 border-slate-200'}`} data-testid="group-by-mmg">Per Medico</button>
          <button onClick={() => setGroupBy('area')} className={`px-2.5 py-1 text-[10px] rounded border transition-all ${groupBy === 'area' ? 'bg-brand text-white border-brand' : 'bg-white text-slate-500 border-slate-200'}`} data-testid="group-by-area">Per Area</button>
        </div>
        <div className="flex items-center gap-1.5">
          <MapPin className="w-3.5 h-3.5 text-slate-400" />
          <button onClick={() => setSelectedArea('tutte')} className={`px-2 py-0.5 text-[10px] rounded-full border ${selectedArea === 'tutte' ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-500 border-slate-200'}`} data-testid="area-filter-tutte">Tutte</button>
          <button onClick={() => setSelectedArea('verde')} className={`px-2 py-0.5 text-[10px] rounded-full border ${selectedArea === 'verde' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-emerald-600 border-emerald-200'}`} data-testid="area-filter-verde">Verde</button>
          <button onClick={() => setSelectedArea('gialla')} className={`px-2 py-0.5 text-[10px] rounded-full border ${selectedArea === 'gialla' ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-amber-600 border-amber-200'}`} data-testid="area-filter-gialla">Gialla</button>
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-3.5 h-3.5 text-slate-400" />
          <Select value={selectedMmg} onValueChange={setSelectedMmg}>
            <SelectTrigger className="h-7 w-[180px] text-xs" data-testid="mmg-filter"><SelectValue placeholder="Filtra per medico" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="tutti">Tutti i medici</SelectItem>
              {mmgList.map(m => (<SelectItem key={m.id} value={m.id}>Dott. {m.cognome} {m.nome}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400">Caricamento...</div>
      ) : filteredItems.length === 0 ? (
        <Card><CardContent className="py-12 text-center">
          <Package className="w-12 h-12 text-emerald-300 mx-auto mb-3" />
          <p className="text-slate-500">Nessun farmaco in esaurimento</p>
          <p className="text-xs text-slate-400 mt-1">Tutte le scorte sono sufficienti</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([key, group]) => (
            <Card key={key} className={groupBy === 'area' ? `border-l-4 ${key === 'verde' ? 'border-l-emerald-500' : key === 'gialla' ? 'border-l-amber-500' : 'border-l-slate-300'}` : ''}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    {groupBy === 'mmg' ? <Pill className="w-4 h-4 text-blue-500" /> : <MapPin className="w-4 h-4 text-slate-500" />}
                    {groupBy === 'mmg' ? `Dott. ${group.label || 'N/D'}` : group.label}
                    <Badge variant="outline" className="text-[10px] ml-2">{group.items.length} farmaci</Badge>
                  </CardTitle>
                  <Button size="sm" variant="outline" onClick={() => handlePrint(key)} data-testid={`print-ordine-${key}`}>
                    <Printer className="w-4 h-4 mr-1" />Stampa Richiesta
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Ospite</TableHead>
                      <TableHead>{groupBy === 'mmg' ? 'Camera / Area' : 'Camera'}</TableHead>
                      <TableHead>Farmaco</TableHead>
                      <TableHead>Dosaggio</TableHead>
                      <TableHead>Scorta</TableHead>
                      <TableHead>Conf.</TableHead>
                      <TableHead>Giorni Rim.</TableHead>
                      <TableHead>Stato</TableHead>
                      <TableHead>Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {group.items.map((item, i) => (
                      <TableRow key={i}
                        className={item.richiesta_inviata ? 'bg-emerald-50/50 border-l-2 border-l-emerald-400' : item.giorni_rimasti <= 3 ? 'bg-red-50' : item.giorni_rimasti <= 7 ? 'bg-amber-50/50' : ''}
                        data-testid={`ordine-row-${item.terapia_id}`}>
                        <TableCell className="font-medium text-sm">{item.ospite_nome}</TableCell>
                        <TableCell className="text-sm">
                          {item.stanza || '-'}/{item.letto || '-'}
                          {groupBy === 'mmg' && item.area && <Badge variant="outline" className={`text-[9px] ml-1.5 capitalize ${areaColors[item.area] || ''}`}>{item.area}</Badge>}
                        </TableCell>
                        <TableCell className="text-sm">
                          <span className="font-medium">{item.farmaco}</span>
                          {item.principio_attivo && <span className="block text-[10px] text-slate-400">{item.principio_attivo}</span>}
                        </TableCell>
                        <TableCell className="text-sm">{item.dosaggio}</TableCell>
                        <TableCell>
                          <span className={`text-sm font-bold ${item.scorta_attuale <= 0 ? 'text-red-600' : 'text-amber-600'}`}>
                            {item.scorta_attuale} u.
                          </span>
                        </TableCell>
                        <TableCell>
                          {item.confezione_unita > 0 ? (
                            <span className="text-xs text-slate-600">{item.confezione_unita} u/conf</span>
                          ) : (
                            <span className="text-[10px] text-slate-300">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={`text-[10px] ${item.giorni_rimasti <= 3 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                            <AlertTriangle className="w-3 h-3 mr-1" />{item.giorni_rimasti} gg
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {item.richiesta_inviata ? (
                            <div>
                              <Badge className="bg-emerald-100 text-emerald-700 text-[10px]"><CheckCircle className="w-3 h-3 mr-0.5" />Richiesto</Badge>
                              {item.data_richiesta && <p className="text-[9px] text-slate-400 mt-0.5">{new Date(item.data_richiesta).toLocaleDateString('it-IT')}</p>}
                            </div>
                          ) : (
                            <Badge className="bg-slate-100 text-slate-500 text-[10px]">Da richiedere</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {!item.richiesta_inviata && hasRole('coordinatore', 'infermiere', 'medico') && (
                              <Button variant="ghost" size="sm" className="h-7 text-xs text-brand hover:text-brand-dark" onClick={() => handleSegnaRichiesta(item.terapia_id)} data-testid={`segna-richiesta-${item.terapia_id}`} title="Segna come richiesto al medico">
                                <Send className="w-3.5 h-3.5 mr-1" />Richiedi
                              </Button>
                            )}
                            {hasRole('coordinatore', 'infermiere') && (
                              <Button variant="ghost" size="sm" className="h-7 text-xs text-emerald-600 hover:text-emerald-700" onClick={() => openRifornimento(item)} data-testid={`rifornimento-${item.terapia_id}`} title="Registra arrivo farmaco">
                                <PackagePlus className="w-3.5 h-3.5 mr-1" />Arrivo
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ))}
          <p className="text-xs text-slate-400 text-center">Totale farmaci in esaurimento: {filteredItems.length}</p>
        </div>
      )}

      {/* Dialog rifornimento singolo */}
      <Dialog open={showRifornimento} onOpenChange={setShowRifornimento}>
        <DialogContent className="max-w-sm" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><PackagePlus className="w-5 h-5 text-emerald-600" />Registra Arrivo Farmaco</DialogTitle></DialogHeader>
          {rifItem && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-3 text-sm space-y-1">
                <p><strong>{rifItem.farmaco}</strong> - {rifItem.dosaggio}</p>
                <p className="text-xs text-slate-400">Ospite: {rifItem.ospite_nome}</p>
                <p className="text-xs text-slate-400">Scorta attuale: <strong className="text-amber-600">{rifItem.scorta_attuale} unita</strong></p>
                {rifItem.confezione_unita > 0 && (
                  <p className="text-xs text-brand">Confezione: {rifItem.confezione_unita} unita/conf.</p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">N. Scatole ricevute</Label>
                  <Input type="number" min="1" value={rifScatole} onChange={e => setRifScatole(e.target.value)} data-testid="rifornimento-scatole" placeholder="Es. 2" autoFocus />
                </div>
                <div>
                  <Label className="text-xs flex items-center gap-1">
                    Unita/Confezione
                    {rifItem.confezione_unita > 0 && !rifManuale && <Badge className="text-[8px] bg-emerald-100 text-emerald-700 ml-1">auto</Badge>}
                  </Label>
                  <Input
                    type="number" min="1"
                    value={rifUnitaConf}
                    onChange={e => { setRifUnitaConf(e.target.value); setRifManuale(true); }}
                    data-testid="rifornimento-unita-conf"
                    placeholder="Es. 30"
                    className={rifItem.confezione_unita > 0 && !rifManuale ? 'border-emerald-300 bg-emerald-50/30' : ''}
                  />
                </div>
              </div>
              {rifTotaleUnita() > 0 && (
                <div className="bg-emerald-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-500">Totale unita da aggiungere</p>
                  <p className="text-xl font-bold text-emerald-700">+{rifTotaleUnita()}</p>
                  <p className="text-xs text-emerald-600">Nuova scorta: {rifItem.scorta_attuale + rifTotaleUnita()} unita</p>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowRifornimento(false)}>Annulla</Button>
                <Button data-testid="save-rifornimento" onClick={handleRifornimento} disabled={rifTotaleUnita() <= 0} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  <PackagePlus className="w-4 h-4 mr-1" />Registra Arrivo
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Nuova Terapia */}
      <Dialog open={showNewTerapia} onOpenChange={setShowNewTerapia}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Plus className="w-5 h-5 text-brand" />Nuova Terapia</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Ospite *</Label>
              <Select value={newForm.ospite_id} onValueChange={v => setNewForm(f => ({ ...f, ospite_id: v }))}>
                <SelectTrigger data-testid="new-terapia-ospite"><SelectValue placeholder="Seleziona ospite..." /></SelectTrigger>
                <SelectContent>
                  {ospiti.map(o => (
                    <SelectItem key={o.id} value={o.id}>{o.cognome} {o.nome} - Cam. {o.stanza}/{o.letto} {o.area ? `(${o.area})` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Farmaco *</Label>
              <FarmacoAutocomplete
                value={newForm.farmaco}
                onChange={v => setNewForm(f => ({ ...f, farmaco: v }))}
                onSelectFull={(item, dos) => {
                  const detected = detectViaAndUnit(item.forme);
                  const liquidUnits = ['gocce', 'ml', 'ml di sciroppo', 'puff'];
                  const isLiquid = liquidUnits.some(u => detected.unitLabel.includes(u));
                  const autoPos = detected.unitLabel !== 'unita' && !isLiquid ? `1 ${detected.unitLabel}` : '';
                  setNewForm(f => ({ ...f, farmaco: `${item.nome} ${dos}`, dosaggio: autoPos, principio_attivo: item.principio_attivo || '', via: detected.via, _unitLabel: detected.unitLabel, _dosPlaceholder: detected.placeholder, _formaFarm: (item.forme || [])[0] || '' }));
                }}
                placeholder="Cerca farmaco AIFA..."
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Posologia (quantita) *</Label>
                <Input value={newForm.dosaggio} onChange={e => setNewForm(f => ({ ...f, dosaggio: e.target.value }))} data-testid="new-terapia-dosaggio" placeholder={newForm._dosPlaceholder || 'es. 1 compressa / 1 fiala'} />
                {newForm._formaFarm && <p className="text-[10px] text-blue-500 mt-0.5">Forma: {newForm._formaFarm}</p>}
              </div>
              <div><Label className="text-xs">Via {newForm._formaFarm && <span className="text-[10px] text-emerald-500 font-normal">(auto)</span>}</Label>
                <Select value={newForm.via} onValueChange={v => setNewForm(f => ({ ...f, via: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['orale','sublinguale','intramuscolare','endovenosa','sottocutanea','transdermica','rettale','inalatoria','topica','oftalmica','altro'].map(v => <SelectItem key={v} value={v} className="capitalize">{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-xs">Principio Attivo</Label><Input value={newForm.principio_attivo} onChange={e => setNewForm(f => ({ ...f, principio_attivo: e.target.value }))} className="text-xs" /></div>
            <div className="flex items-center gap-3 p-3 rounded-lg border border-dashed border-amber-300 bg-amber-50/50">
              <input type="checkbox" checked={newForm.al_bisogno} onChange={e => setNewForm(f => ({ ...f, al_bisogno: e.target.checked, orari: e.target.checked ? [] : f.orari }))} className="w-4 h-4 rounded border-amber-400 text-amber-600" data-testid="new-terapia-ab" />
              <div><Label className="text-xs font-semibold text-amber-800 cursor-pointer">Terapia Al Bisogno (AB)</Label><p className="text-[10px] text-amber-600 mt-0.5">Senza orari fissi</p></div>
            </div>
            {!newForm.al_bisogno && (
              <div>
                <Label className="text-xs">Orari Somministrazione *</Label>
                <div className="flex gap-1.5 flex-wrap mt-1">
                  {ORARI_PRESET.map(o => (
                    <button key={o} type="button" onClick={() => toggleOrario(o)}
                      className={`px-2.5 py-1 text-xs rounded-md border font-mono ${newForm.orari.includes(o) ? 'bg-brand text-white border-brand' : 'bg-white text-slate-600 border-slate-200'}`}>{o}</button>
                  ))}
                </div>
              </div>
            )}
            <div>
              <Label className="text-xs">Data Fine Terapia</Label>
              <div className="flex items-center gap-2 mt-1">
                <button type="button" onClick={() => setNewForm(f => ({ ...f, durata_fine: 'mai' }))}
                  className={`px-3 py-1.5 text-xs rounded-md border transition-all font-medium ${newForm.durata_fine === 'mai' ? 'bg-brand text-white border-brand' : 'bg-white text-slate-500 border-slate-200 hover:border-blue-300'}`}>Mai (a vita)</button>
                <button type="button" onClick={() => setNewForm(f => ({ ...f, durata_fine: f.durata_fine === 'mai' ? new Date(Date.now() + 7*86400000).toISOString().slice(0, 10) : f.durata_fine }))}
                  className={`px-3 py-1.5 text-xs rounded-md border transition-all font-medium ${newForm.durata_fine !== 'mai' ? 'bg-brand text-white border-brand' : 'bg-white text-slate-500 border-slate-200 hover:border-blue-300'}`}>Data specifica</button>
              </div>
              {newForm.durata_fine !== 'mai' && (
                <DatePickerField value={newForm.durata_fine} onChange={v => setNewForm(f => ({ ...f, durata_fine: v }))} className="mt-1.5" />
              )}
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Scorta Attuale</Label><Input type="number" value={newForm.scorta_attuale} onChange={e => setNewForm(f => ({ ...f, scorta_attuale: e.target.value }))} data-testid="new-terapia-scorta" /></div>
              <div><Label className="text-xs">U./Confezione</Label><Input type="number" value={newForm.confezione_unita} onChange={e => setNewForm(f => ({ ...f, confezione_unita: e.target.value }))} /></div>
              <div><Label className="text-xs">Soglia (gg)</Label><Input type="number" value={newForm.soglia_riordino} onChange={e => setNewForm(f => ({ ...f, soglia_riordino: e.target.value }))} /></div>
            </div>
            <div><Label className="text-xs">Note</Label><Input value={newForm.note} onChange={e => setNewForm(f => ({ ...f, note: e.target.value }))} /></div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowNewTerapia(false)}>Annulla</Button>
              <Button onClick={handleSaveNewTerapia} className="bg-brand hover:bg-brand-dark text-white" data-testid="save-new-terapia-ordini">
                <Plus className="w-4 h-4 mr-1" />Salva Terapia
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <RicevimentoFarmaciDialog
        open={ricevimentoOpen}
        onOpenChange={setRicevimentoOpen}
        ospiteId={ricevimentoOspiteId}
        onComplete={fetchData}
      />
    </div>
  );
}
